/**
 * Review stage for any model-drafted prompt: always editable, never auto-applied.
 *
 * Shared by the campaign-context panel and "Enrich my prompt", so it knows nothing about
 * where the draft came from — only that a model wrote it and a human has to approve it. **A
 * drafted prompt never becomes the real prompt without an explicit action**, which is why
 * there is no auto-apply path and no way to close this without choosing.
 *
 * The textarea stays editable while the guardrail is unacknowledged, on purpose: editing is
 * the *other* way out of a warning, and locking the field would leave a flagged draft with
 * nothing to do but discard it.
 *
 * **Why the acknowledgement is local, and why it is not the final gate.** Unlike the
 * describe/edit gates — whose acknowledgement lives in the page because Generate reads it —
 * this draft is transient review-stage content that may be discarded and never generated
 * from. Approving does not authorise generation: the approved text lands in the page's brief,
 * where the page's own gate re-evaluates it independently, and the server re-checks the exact
 * final string again. Because the page stores its acknowledgement as *the text that was
 * acknowledged*, an approved flagged prompt arrives there un-acknowledged and must be ticked
 * again. That is intentional — the second tick is against the brief the user is actually about
 * to generate from, which is the one the record needs to show they reviewed.
 *
 * Keyed to the text for the same reason the page's is: editing after ticking invalidates the
 * acknowledgement in the same render, so the box can never be stale-ticked for text nobody
 * read.
 */

import { Button, Card, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useId, useMemo, useState } from "react";

import type { GuardrailConfig } from "@/guardrail/rules";
import { detectSensitiveTopics } from "@/guardrail/rules";

import { GuardrailGate } from "./GuardrailGate";

export interface PromptReviewCardProps {
  /** What kind of draft this is, e.g. "Enriched prompt". Shown as the heading. */
  label: string;
  draft: string;
  onDraftChange: (value: string) => void;
  /** "Use this prompt" — commits the draft into the page's brief. */
  onApprove: () => void;
  /** Closes without applying. */
  onDiscard: () => void;
  /** True while the model call is in flight. */
  loading: boolean;
  config: GuardrailConfig | undefined;
}

export function PromptReviewCard({
  label,
  draft,
  onDraftChange,
  onApprove,
  onDiscard,
  loading,
  config,
}: PromptReviewCardProps) {
  /*
   * The guardrail on the *drafted* text — its own check rather than a reuse of the page's,
   * because a model can introduce trigger words that were not in the source copy, or strip
   * ones that were.
   */
  const match = useMemo(() => detectSensitiveTopics(draft, config), [draft, config]);

  const [acknowledgedDraft, setAcknowledgedDraft] = useState<string | null>(null);
  const acknowledged = acknowledgedDraft !== null && acknowledgedDraft === draft;

  const canApprove = !loading && draft.trim().length > 0 && !(match.flagged && !acknowledged);

  /*
   * Generated rather than fixed, because two of these cards can genuinely be on screen at
   * once: the campaign panel can sit open with a draft under review while the user also
   * clicks "Enrich my prompt". Fixed ids would be duplicated in the DOM and every label would
   * resolve to whichever card rendered first — so clicking the second card's checkbox label
   * would tick the first card's box.
   */
  const reactId = useId();

  return (
    <Card sx={{ p: 2.5, border: "1px solid rgba(51,105,255,0.3)" }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="overline" color="primary.dark">
            {label}
          </Typography>
          {loading ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Drafting your prompt…
              </Typography>
            </Stack>
          ) : null}
        </Stack>

        <TextField
          id={`prompt-review-draft-${reactId}`}
          label="Edit anything you like before using it"
          multiline
          minRows={5}
          value={draft}
          disabled={loading}
          error={match.flagged}
          helperText="A model wrote this from your copy. Nothing is generated until you use it."
          onChange={(event) => onDraftChange(event.target.value)}
        />

        <GuardrailGate
          id={`guardrail-prompt-review-${reactId}`}
          match={match}
          config={config}
          acknowledged={acknowledged}
          onAcknowledgeChange={(value) => setAcknowledgedDraft(value ? draft : null)}
          disabled={loading}
        />

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" disabled={!canApprove} onClick={onApprove}>
            Use this prompt
          </Button>
          <Button variant="text" disabled={loading} onClick={onDiscard}>
            Discard
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
